import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconHeadphones,
  IconLoader2,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconPlaylist,
  IconRewindBackward15,
  IconRewindForward15,
  IconX,
} from "@tabler/icons-react";
import { cn } from "../../../lib/utils";
import { formatTime } from "../../../tools/utils";
import { useCountdown } from "../../../hooks/use-countdown";

/** The passage being read, when the reader is on the article that plays. */
export type SidebarNowReading = {
  title: string;
  /** Where that passage sits against the page's viewport. */
  position: "above" | "visible" | "below";
  onReveal: () => void;
};

type TrackHeading = {
  title: string;
  /** The line under the title: "Audio summary · journal", or the collection. */
  source: string;
  /** Opens what plays (the article, at the passage being read). Null for a
   *  track with no page of its own. */
  onOpen: (() => void) | null;
};

/** One state of the player, as Figma "Audio Player / Sidebar" draws it. */
export type SidebarAudioPlayerView =
  | (TrackHeading & {
      kind: "track";
      isPlaying: boolean;
      nowReading: SidebarNowReading | null;
      elapsed: number;
      /** Null while the track has no known length (a live stream). */
      duration: number | null;
      speed: number;
      onPlayPause: () => void;
      /** Null while the track cannot seek (a live stream). */
      onSeek: ((time: number) => void) | null;
      onSkipBack: (() => void) | null;
      onSkipForward: (() => void) | null;
      onCycleSpeed: () => void;
      /** Null when nothing follows in the queue. */
      onNext: (() => void) | null;
      onClose: () => void;
    })
  | (TrackHeading & { kind: "preparing"; onClose: () => void })
  | (TrackHeading & {
      kind: "failed";
      onRetry: () => void;
      onClose: () => void;
    })
  | (TrackHeading & {
      kind: "up-next";
      /** Seconds before the next article plays (see `UpNextCountdown`). */
      secondsLeft: number;
      /** Share of the wait already elapsed, 0 to 1. */
      waited: number;
      onPlayNow: () => void;
      onCancel: () => void;
    });

const controlButtonClass =
  "flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-ds-moss-100 transition-colors hover:text-white disabled:cursor-default disabled:opacity-40";

function Equalizer({ isPlaying }: { isPlaying: boolean }): React.JSX.Element {
  // Bar heights of the Figma equalizer: 6, 10 and 7 pixels.
  const bars = ["h-1.5", "h-2.5", "h-1.75"] as const;
  return (
    <span aria-hidden className="flex size-2.5 shrink-0 items-end gap-0.5">
      {bars.map((height, index) => (
        <span
          key={height}
          style={{ animationDelay: `${index * 150}ms` }}
          className={cn(
            "w-0.5 origin-bottom rounded-xs bg-ds-moss-300",
            height,
            isPlaying ? "animate-equalizer" : null
          )}
        />
      ))}
    </span>
  );
}

function Heading({
  heading,
  equalizer,
  onClose,
}: {
  heading: TrackHeading;
  /** Null hides it: nothing plays yet. */
  equalizer: { isPlaying: boolean } | null;
  onClose: (() => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const headingTextClass = "flex min-w-0 flex-1 flex-col items-start gap-0.5";
  const text = (
    <>
      <span className="line-clamp-2 text-footnote font-semibold text-white">
        {heading.title}
      </span>
      <span className="flex w-full min-w-0 items-center gap-1.5">
        {equalizer === null ? null : (
          <Equalizer isPlaying={equalizer.isPlaying} />
        )}
        <span className="truncate text-caption text-ds-moss-300">
          {heading.source}
        </span>
      </span>
    </>
  );
  return (
    <div className="flex w-full items-start gap-2">
      {heading.onOpen === null ? (
        <div className={headingTextClass}>{text}</div>
      ) : (
        <button
          type="button"
          onClick={heading.onOpen}
          className={cn(
            headingTextClass,
            "cursor-pointer border-none bg-transparent p-0 text-left"
          )}
        >
          {text}
        </button>
      )}
      {onClose === null ? null : (
        <button
          type="button"
          onClick={onClose}
          aria-label={t("Close the player")}
          title={t("Close the player")}
          className={cn(controlButtonClass, "size-6 shrink-0 rounded-full")}
        >
          <IconX className="size-4" stroke={1.5} />
        </button>
      )}
    </div>
  );
}

function NowReadingRow({
  nowReading,
}: {
  nowReading: SidebarNowReading;
}): React.JSX.Element {
  const { t } = useTranslation();
  const Arrow =
    nowReading.position === "above"
      ? IconArrowUp
      : nowReading.position === "below"
        ? IconArrowDown
        : null;
  const content = (
    <>
      <IconHeadphones
        className="size-4 shrink-0 text-ds-moss-300"
        stroke={1.25}
      />
      {/* The section name is what the row is for: in a language with a long
          label, the label gives way first. */}
      <span className="min-w-0 truncate text-caption text-ds-moss-300">
        {`${t("Now reading")} ·`}
      </span>
      <span className="shrink-0 text-left text-caption font-medium text-ds-moss-100">
        {nowReading.title}
      </span>
      <span className="flex-1" />
      {Arrow === null ? null : (
        <Arrow className="size-4 shrink-0 text-ds-moss-100" stroke={1.5} />
      )}
    </>
  );
  const rowClass =
    "flex w-full items-center gap-1.5 rounded-lg bg-white/8 px-2 py-1";
  // A link only while the passage is off screen: on screen, there is nowhere
  // to go.
  return Arrow === null ? (
    <div className={rowClass}>{content}</div>
  ) : (
    <button
      type="button"
      onClick={nowReading.onReveal}
      title={t("Go to the passage being read")}
      className={cn(rowClass, "cursor-pointer border-none hover:bg-white/12")}
    >
      {content}
    </button>
  );
}

/**
 * The seek bar: click or drag anywhere on it. The playhead grows on hover.
 * Without `onSeek` (nothing to seek within yet), it only shows the progress.
 */
function SeekBar({
  fraction,
  onSeek,
  label,
}: {
  fraction: number | null;
  onSeek: ((fraction: number) => void) | null;
  label: string;
}): React.JSX.Element {
  const barRef = useRef<HTMLDivElement>(null);
  // The position under the pointer while it drags, shown before it is
  // committed, so the playhead follows the hand rather than the audio.
  const [dragFraction, setDragFraction] = useState<number | null>(null);
  const shown = dragFraction === null ? fraction : dragFraction;

  function fractionAt(clientX: number): number {
    const bar = barRef.current;
    if (bar === null) throw new Error("should not happen: no seek bar");
    const box = bar.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - box.left) / box.width));
  }

  return (
    <div
      ref={barRef}
      role="slider"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={shown === null ? 0 : Math.round(shown * 100)}
      aria-disabled={onSeek === null}
      tabIndex={onSeek === null ? -1 : 0}
      onPointerDown={
        onSeek === null
          ? undefined
          : (event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              setDragFraction(fractionAt(event.clientX));
            }
      }
      onPointerMove={(event) => {
        if (dragFraction === null) return;
        setDragFraction(fractionAt(event.clientX));
      }}
      onPointerUp={(event) => {
        if (dragFraction === null || onSeek === null) return;
        onSeek(fractionAt(event.clientX));
        setDragFraction(null);
      }}
      onPointerCancel={() => {
        setDragFraction(null);
      }}
      onKeyDown={(event) => {
        if (onSeek === null || shown === null) return;
        if (event.key === "ArrowRight") onSeek(Math.min(1, shown + 0.05));
        if (event.key === "ArrowLeft") onSeek(Math.max(0, shown - 0.05));
      }}
      className={cn(
        "group relative flex h-2.5 w-full items-center outline-none",
        onSeek === null ? null : "cursor-pointer"
      )}
    >
      <div className="relative h-0.75 w-full overflow-hidden rounded-xs bg-white/18">
        {shown === null ? null : (
          <div
            // A playback position, not a design value.
            style={{ width: `${shown * 100}%` }}
            className="absolute inset-y-0 left-0 bg-white"
          />
        )}
      </div>
      {shown === null ? null : (
        <span
          style={{ left: `${shown * 100}%` }}
          className="absolute size-1.5 -translate-x-1/2 rounded-full bg-white transition-[width,height] group-hover:size-2.5 group-focus-visible:size-2.5"
        />
      )}
    </div>
  );
}

function SpeedButton({
  speed,
  onClick,
}: {
  speed: number;
  onClick: (() => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick === null ? undefined : onClick}
      disabled={onClick === null}
      aria-label={t("Playback speed")}
      title={t("Playback speed")}
      className={cn(
        controlButtonClass,
        "h-7 w-10 rounded-lg bg-white/8 text-footnote font-semibold"
      )}
    >
      {`${speed}×`}
    </button>
  );
}

function Transport({
  center,
  onSkipBack,
  onSkipForward,
}: {
  center: React.ReactNode;
  onSkipBack: (() => void) | null;
  onSkipForward: (() => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onSkipBack === null ? undefined : onSkipBack}
        disabled={onSkipBack === null}
        aria-label={t("Back 15 seconds")}
        title={t("Back 15 seconds")}
        className={cn(controlButtonClass, "size-8 rounded-full")}
      >
        <IconRewindBackward15 className="size-5" stroke={1.5} />
      </button>
      {center}
      <button
        type="button"
        onClick={onSkipForward === null ? undefined : onSkipForward}
        disabled={onSkipForward === null}
        aria-label={t("Forward 15 seconds")}
        title={t("Forward 15 seconds")}
        className={cn(controlButtonClass, "size-8 rounded-full")}
      >
        <IconRewindForward15 className="size-5" stroke={1.5} />
      </button>
    </div>
  );
}

function NextButton({
  onNext,
}: {
  onNext: (() => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onNext === null ? undefined : onNext}
      disabled={onNext === null}
      aria-label={t("Play the next article")}
      title={t("Play the next article")}
      className={cn(controlButtonClass, "h-7 w-10 rounded-lg")}
    >
      <IconPlaylist className="size-5" stroke={1.5} />
    </button>
  );
}

function UpNextCard({
  view,
}: {
  view: Extract<SidebarAudioPlayerView, { kind: "up-next" }>;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <span className="text-caption font-medium text-ds-moss-100">
        {t("Up next in {{count}} s", { count: view.secondsLeft })}
      </span>
      <Heading heading={view} equalizer={null} onClose={null} />
      <div className="relative h-0.75 w-full overflow-hidden rounded-xs bg-white/18">
        <div
          // A countdown position, not a design value.
          style={{ width: `${view.waited * 100}%` }}
          className="absolute inset-y-0 left-0 bg-white"
        />
      </div>
      {/* Each label stays on one line; in a language where both do not fit
          side by side, Play now drops under Cancel. */}
      <div className="flex w-full flex-wrap gap-2">
        <button
          type="button"
          onClick={view.onCancel}
          className="flex flex-auto cursor-pointer whitespace-nowrap items-center justify-center rounded-lg border-none bg-white/8 px-3 py-1.5 text-footnote font-semibold text-ds-moss-100 hover:bg-white/12"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={view.onPlayNow}
          className="flex flex-auto cursor-pointer whitespace-nowrap items-center justify-center gap-1.5 rounded-lg border-none bg-white px-3 py-1.5 text-footnote font-semibold text-ds-moss-950 hover:bg-ds-moss-50"
        >
          <IconPlayerPlayFilled className="size-4" />
          {t("Play now")}
        </button>
      </div>
    </>
  );
}

/**
 * Runs the Up next countdown to `endsAt` and plays the next article then. It
 * wraps the player rather than living in its card, so it keeps running while
 * the sidebar is collapsed.
 */
export function UpNextCountdown({
  endsAt,
  totalMs,
  onDone,
  children,
}: {
  endsAt: number;
  totalMs: number;
  onDone: () => void;
  children: (countdown: {
    secondsLeft: number;
    waited: number;
  }) => React.ReactNode;
}): React.ReactNode {
  const left = useCountdown(endsAt, onDone);
  return children({
    secondsLeft: Math.ceil(left / 1000),
    waited: Math.min(1, 1 - left / totalMs),
  });
}

/**
 * The desktop audio player at the bottom of the sidebar, above the profile
 * (Figma "Audio Player / Sidebar"). It belongs to the shell, so it stays when
 * the reader changes page. Presentational: the caller maps the listening
 * session onto `view`.
 */
export function SidebarAudioPlayer({
  view,
}: {
  view: SidebarAudioPlayerView;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col items-start gap-2 overflow-hidden rounded-xl bg-ds-moss-900 p-3">
      {((): React.ReactNode => {
        switch (view.kind) {
          case "track": {
            const fraction =
              view.duration === null
                ? null
                : Math.min(1, view.elapsed / view.duration);
            const onSeek = view.onSeek;
            const duration = view.duration;
            return (
              <>
                <Heading
                  heading={view}
                  equalizer={{ isPlaying: view.isPlaying }}
                  onClose={view.onClose}
                />
                {view.nowReading === null ? null : (
                  <NowReadingRow nowReading={view.nowReading} />
                )}
                <div className="flex w-full flex-col gap-0.5">
                  <SeekBar
                    fraction={fraction}
                    label={t("Playback position")}
                    onSeek={
                      onSeek === null || duration === null
                        ? null
                        : (value) => {
                            onSeek(value * duration);
                          }
                    }
                  />
                  <div className="flex w-full justify-between text-caption text-ds-moss-300">
                    <span>{formatTime(view.elapsed)}</span>
                    <span>
                      {duration === null
                        ? "--:--"
                        : `−${formatTime(Math.max(0, duration - view.elapsed))}`}
                    </span>
                  </div>
                </div>
                <div className="flex w-full items-center justify-between">
                  <SpeedButton speed={view.speed} onClick={view.onCycleSpeed} />
                  <Transport
                    onSkipBack={view.onSkipBack}
                    onSkipForward={view.onSkipForward}
                    center={
                      <button
                        type="button"
                        onClick={view.onPlayPause}
                        aria-label={view.isPlaying ? t("Pause") : t("Play")}
                        title={view.isPlaying ? t("Pause") : t("Play")}
                        className="flex size-9 cursor-pointer items-center justify-center rounded-full border-none bg-white p-0 text-ds-moss-950 hover:bg-ds-moss-50"
                      >
                        {view.isPlaying ? (
                          <IconPlayerPauseFilled className="size-5" />
                        ) : (
                          <IconPlayerPlayFilled className="size-5" />
                        )}
                      </button>
                    }
                  />
                  <NextButton onNext={view.onNext} />
                </div>
              </>
            );
          }
          case "preparing":
            return (
              <>
                <Heading
                  heading={view}
                  equalizer={null}
                  onClose={view.onClose}
                />
                <div className="flex w-full flex-col gap-0.5">
                  <SeekBar
                    fraction={null}
                    onSeek={null}
                    label={t("Playback position")}
                  />
                  <span className="text-caption text-ds-moss-300">
                    {t("Preparing audio…")}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <SpeedButton speed={1} onClick={null} />
                  <Transport
                    onSkipBack={null}
                    onSkipForward={null}
                    center={
                      <span
                        role="status"
                        aria-label={t("Preparing audio…")}
                        className="flex size-9 items-center justify-center rounded-full bg-white text-ds-moss-950"
                      >
                        <IconLoader2 className="size-5 animate-spin" />
                      </span>
                    }
                  />
                  <NextButton onNext={null} />
                </div>
              </>
            );
          case "failed":
            return (
              <>
                <Heading
                  heading={view}
                  equalizer={null}
                  onClose={view.onClose}
                />
                <div className="flex w-full flex-col gap-0.5">
                  <SeekBar
                    fraction={null}
                    onSeek={null}
                    label={t("Playback position")}
                  />
                  <p role="alert" className="m-0 text-caption text-white">
                    {`${t("Couldn't play the audio")} · `}
                    <button
                      type="button"
                      onClick={view.onRetry}
                      className="cursor-pointer border-none bg-transparent p-0 text-caption text-white underline"
                    >
                      {t("Retry")}
                    </button>
                  </p>
                </div>
              </>
            );
          case "up-next":
            return <UpNextCard view={view} />;
          default:
            throw new Error(view satisfies never);
        }
      })()}
    </div>
  );
}

/** One state of the collapsed player (Figma "Audio Player / Rail"). */
export type SidebarAudioRailView = {
  title: string;
  /** Under the title in the hover preview, e.g. "Audio summary · 1:46 left". */
  subtitle: string;
  control:
    | { kind: "track"; isPlaying: boolean; onPlayPause: () => void }
    | { kind: "preparing" }
    | { kind: "attention" };
  /** Playback fraction, 0 to 1. Null draws the ring empty. */
  progress: number | null;
  /** Opens the sidebar on the full player. */
  onExpand: () => void;
};

// Geometry of the progress ring, in the 48-unit box of the SVG.
const RING_RADIUS = 22;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * The player on the collapsed sidebar, above the avatar: a play/pause button
 * in a progress ring. Hovering it previews the title and the time left; the
 * preview opens the sidebar on the full player.
 */
export function SidebarAudioRail({
  view,
}: {
  view: SidebarAudioRailView;
}): React.JSX.Element {
  const { t } = useTranslation();
  const control = view.control;
  return (
    <div className="group relative size-12">
      <svg
        aria-hidden
        viewBox="0 0 48 48"
        className="absolute inset-0 size-12 -rotate-90"
      >
        <circle
          cx="24"
          cy="24"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="2"
          className="stroke-ds-moss-200"
        />
        {view.progress === null ? null : (
          <circle
            cx="24"
            cy="24"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={RING_LENGTH}
            strokeDashoffset={RING_LENGTH * (1 - view.progress)}
            className="stroke-ds-moss-900"
          />
        )}
      </svg>
      {((): React.ReactNode => {
        const buttonClass =
          "absolute top-1 left-1 flex size-10 cursor-pointer items-center justify-center rounded-full border-none bg-ds-moss-900 p-0 text-white";
        switch (control.kind) {
          case "track":
            return (
              <button
                type="button"
                onClick={control.onPlayPause}
                aria-label={control.isPlaying ? t("Pause") : t("Play")}
                className={buttonClass}
              >
                {control.isPlaying ? (
                  <IconPlayerPauseFilled className="size-5" />
                ) : (
                  <IconPlayerPlayFilled className="size-5" />
                )}
              </button>
            );
          case "preparing":
            return (
              <button
                type="button"
                onClick={view.onExpand}
                aria-label={t("Preparing audio…")}
                className={buttonClass}
              >
                <IconLoader2 className="size-5 animate-spin" />
              </button>
            );
          case "attention":
            return (
              <button
                type="button"
                onClick={view.onExpand}
                aria-label={view.subtitle}
                className={buttonClass}
              >
                <IconAlertCircle className="size-5" stroke={1.5} />
              </button>
            );
          default:
            throw new Error(control satisfies never);
        }
      })()}
      {/* The preview sits in the page beside the rail; `pl-4` bridges the
          gap so the pointer can travel onto it without it closing. */}
      <div className="invisible absolute bottom-0 left-full z-20 pl-4 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-has-focus-visible:visible group-has-focus-visible:opacity-100">
        <button
          type="button"
          onClick={view.onExpand}
          className="flex w-60 cursor-pointer flex-col items-start gap-0.5 rounded-lg border-none bg-ds-moss-900 px-3 py-2 text-left shadow-popover"
        >
          <span className="w-full truncate text-footnote font-semibold text-white">
            {view.title}
          </span>
          <span className="w-full truncate text-caption text-ds-moss-300">
            {view.subtitle}
          </span>
        </button>
      </div>
    </div>
  );
}
